/**
 * Social Proof Path Converter
 *
 * Bidirectional conversion between tree node keys and social proof paths.
 * Path format: @review[ID], @rating[ID], @studyCase[ID], @bigNumber[ID]
 */

import type { SocialProofTreeNode, SocialProofCategory } from './types';
import {
  findNodeByKey,
  collectAllCheckableKeys,
  isFullyChecked,
  markAsProcessed,
} from '../../shared/treeHelpers';

/**
 * Maps category to path prefix
 */
const CATEGORY_TO_PREFIX: Record<SocialProofCategory, string> = {
  review: '@review',
  rating: '@rating',
  studyCase: '@studyCase',
  bigNumber: '@bigNumber',
};

/**
 * Converts a tree node key to a path string
 */
export function treeNodeIdToPath(nodeKey: string, allNodes: SocialProofTreeNode[]): string | null {
  const node = findNodeByKey(nodeKey, allNodes) as SocialProofTreeNode | null;
  if (!node) return null;

  if (node.nodeType === 'item' && node.entityId) {
    const prefix = CATEGORY_TO_PREFIX[node.category];
    return `${prefix}[${node.entityId}]`;
  }

  // Category nodes don't produce a single path — they expand to all children
  return null;
}

/**
 * Converts checked tree node IDs to optimized paths.
 * If all items in a category are selected, individual paths are emitted
 * (no category-level path — social proof paths are always entity-level).
 */
export function treeNodeIdsToPaths(checkedIds: string[], allNodes: SocialProofTreeNode[]): string[] {
  const paths: string[] = [];

  for (const id of checkedIds) {
    const path = treeNodeIdToPath(id, allNodes);
    if (path) {
      paths.push(path);
    }
  }

  return paths;
}

/**
 * Parses a social proof path to extract category and entity ID
 */
function parseSocialProofPath(path: string): { category: SocialProofCategory; entityId: string } | null {
  const match = path.match(/^@(review|rating|studyCase|bigNumber)\[([^\]]+)\]/);
  if (!match) return null;
  return {
    category: match[1] as SocialProofCategory,
    entityId: match[2],
  };
}

/**
 * Converts a single path to tree node IDs (for hydration)
 */
function pathToTreeNodeIds_single(path: string, allNodes: SocialProofTreeNode[]): string[] {
  const parsed = parseSocialProofPath(path);
  if (!parsed) return [];

  const nodeKey = `${parsed.category}-${parsed.entityId}`;
  const node = findNodeByKey(nodeKey, allNodes);
  if (node) {
    return [nodeKey];
  }
  return [];
}

/**
 * Converts paths to tree node IDs (for hydration/restoring selection state)
 */
export function pathsToTreeNodeIds(paths: string[], allNodes: SocialProofTreeNode[]): string[] {
  const keys = new Set<string>();

  for (const path of paths) {
    const nodeKeys = pathToTreeNodeIds_single(path, allNodes);
    nodeKeys.forEach((key) => keys.add(key));
  }

  return Array.from(keys);
}

/**
 * Smart path deduplication.
 * For social proof, paths are always entity-level (no nested fields),
 * so deduplication simply removes duplicate entity paths.
 */
export function deduplicatePaths(paths: string[]): string[] {
  return [...new Set(paths)];
}
